/**
 * Page d'atterrissage des campagnes de recherche (Google Ads).
 *
 * Pourquoi elle existe plutôt que d'envoyer vers /pricing : quelqu'un qui tape
 * « eagleview alternative » cherche UNE chose — le prix d'une mesure et un
 * bouton pour l'obtenir. La grille complète (réceptionniste en ajout,
 * parrainage) le fait réfléchir au lieu d'acheter.
 *
 * ⚠️ Et surtout : elle vit DANS l'application React, donc le pixel Meta, GA4 et
 * la bannière Loi 25 s'y appliquent. Les pages statiques de `public/` (dont
 * `no-monthly-fee/`) n'ont AUCUN suivi — y envoyer du trafic payant rendrait
 * les clics invisibles et empêcherait toute optimisation.
 *
 * Deux offres, dans cet ordre voulu : l'À la Carte est le héros, parce que
 * l'annonce promet « pas d'abonnement » et qu'ouvrir sur un abonnement
 * contredirait la promesse. L'abonnement est la porte de sortie pour celui qui
 * mesure beaucoup — et le seuil de bascule est affiché honnêtement.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import LangSwitch from "@/components/LangSwitch";
import ThemeSwitch from "@/components/ThemeSwitch";
import Marque from "@/components/Marque";
import { Button } from "@/components/ui/button";
import { Check, Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { evenement } from "@/lib/pixel";
import { evenementGA } from "@/lib/analytics";
import SourcePreferee from "@/components/SourcePreferee";

const CONTACT = "quotes@roofborder.com";
const TEL_AFFICHE = "1-866-4ROOF-21";
const TEL_LIEN = "tel:+18664766321";

// Grille de secours si /plans ne répond pas (mêmes valeurs que PLANS serveur).
const SOLO = { prix_mois: 119, prix_mois_usd: 79, prix_annuel_mois: 88,
               prix_annuel_mois_usd: 59, estimates_per_month: 40 };

const T = {
  en: {
    connexion: "Sign in",
    surtitre: "Satellite roof measurements",
    h1: "Measure a roof for $9.95. No monthly fee.",
    sous: "Put in an address. Forty seconds later you have the total area, every roof face and every pitch — from satellite. No tracing, no ladder, no drive-out.",
    preuves: ["Works on phone, tablet and desktop", "All 50 states and all of Canada", "Branded PDF quote with your logo"],
    carteTitre: "Pay as you Go",
    carteSous: "No subscription. You pay when you measure, and nothing in the months you don't.",
    cartePrix: (p) => `${p} per roof`,
    carteBonus: "First purchase: 2 extra measurements free",
    carteChamp: "Amount to deposit",
    carteMin: (m) => `Minimum ${m}`,
    carteCalc: (n) => `That's ${n} measurements`,
    carteBtn: "Get measuring",
    carteNote: "No card kept on file. No renewal.",
    aboTitre: "Or a monthly plan",
    aboSous: (n) => `If you measure a lot, the Solo plan includes ${n} measurements a month.`,
    aboBascule: (s) => `Above ${s} measurements a month, Solo costs you less than paying per roof.`,
    aboBtn: "See the plans",
    compTitre: "What the others charge",
    comp: [["EagleView", "$15–38 per report"], ["Roofr", "$19 per report"],
           ["GAF QuickMeasure", "$18 per report"], ["RoofSnap", "$49–99 a month"],
           ["Roof Border", "$9.95 per report"]],
    compNote: "Public prices, September 2026. We are the least expensive paid report on the market — and the only one you can use without committing to anything.",
    faq: [
      ["Is there really no subscription?", "None. You put money in your account, each measurement takes $9.95 out of it, and when it runs out nothing happens — no renewal, no card kept on file."],
      ["How accurate is it?", "Test it on a roof you have already been on. That is what the two free measurements of a first purchase are for: compare our numbers with yours before trusting them on a real quote."],
      ["What if the house is not in the imagery?", "You can draw small buildings — sheds, garages, additions — by hand and they are included in the totals."],
      ["Do I need a credit card to try?", "To deposit, yes. There is also a free demo on our home page with no card and no signup."],
    ],
    pied: "Satellite roof measurements for roofing contractors.",
    erreur: "Online payment is unavailable right now. Write to us and we will open your account by hand.",
  },
  fr: {
    connexion: "Connexion",
    surtitre: "Mesures de toiture par satellite",
    h1: "Mesurez un toit pour 14 $. Sans frais mensuels.",
    sous: "Entrez une adresse. Quarante secondes plus tard, vous avez la superficie totale, chaque pan et chaque pente — par satellite. Sans traçage, sans échelle, sans déplacement.",
    preuves: ["Fonctionne sur téléphone, tablette et ordinateur", "Tout le Canada et les 50 États", "Soumission PDF à votre image"],
    carteTitre: "À la Carte",
    carteSous: "Aucun abonnement. Vous payez quand vous mesurez, et rien les mois où vous ne mesurez pas.",
    cartePrix: (p) => `${p} par toit`,
    carteBonus: "Premier achat : 2 mesures gratuites en plus",
    carteChamp: "Montant à déposer",
    carteMin: (m) => `Minimum ${m}`,
    carteCalc: (n) => `Soit ${n} mesures`,
    carteBtn: "Commencer à mesurer",
    carteNote: "Aucune carte conservée. Aucun renouvellement.",
    aboTitre: "Ou un forfait mensuel",
    aboSous: (n) => `Si vous mesurez beaucoup, le forfait Solo comprend ${n} mesures par mois.`,
    aboBascule: (s) => `À partir de ${s} mesures par mois, Solo vous coûte moins cher qu'à la pièce.`,
    aboBtn: "Voir les forfaits",
    compTitre: "Ce que les autres facturent",
    comp: [["EagleView", "15 à 38 $ le rapport"], ["Roofr", "19 $ le rapport"],
           ["GAF QuickMeasure", "18 $ le rapport"], ["RoofSnap", "49 à 99 $ par mois"],
           ["Roof Border", "9,95 $ le rapport"]],
    compNote: "Prix publics en dollars US, septembre 2026. Nous sommes le rapport payant le moins cher du marché — et le seul qui ne vous engage à rien.",
    faq: [
      ["Vraiment aucun abonnement ?", "Aucun. Vous mettez de l'argent dans votre compte, chaque mesure en retire le prix, et quand le solde est vide il ne se passe rien — pas de renouvellement, pas de carte conservée."],
      ["C'est précis à quel point ?", "Testez sur un toit que vous connaissez déjà. C'est exactement à ça que servent les deux mesures gratuites du premier achat : comparez nos chiffres aux vôtres avant de nous faire confiance sur une vraie soumission."],
      ["Et si le bâtiment n'est pas sur l'image ?", "Vous pouvez tracer à la main les petits bâtiments — cabanons, garages, rallonges — et ils entrent dans les totaux."],
      ["Faut-il une carte pour essayer ?", "Pour déposer, oui. Il y a aussi une démonstration gratuite sur notre page d'accueil, sans carte et sans inscription."],
    ],
    pied: "Mesures de toiture par satellite pour les couvreurs.",
    erreur: "Le paiement en ligne est indisponible. Écrivez-nous et nous ouvrirons votre compte à la main.",
  },
  es: {
    connexion: "Iniciar sesión",
    surtitre: "Mediciones de techos por satélite",
    h1: "Mida un techo por $9.95. Sin cuota mensual.",
    sous: "Ingrese una dirección. Cuarenta segundos después tiene el área total, cada faldón y cada pendiente — por satélite. Sin trazar, sin escalera, sin desplazarse.",
    preuves: ["Funciona en teléfono, tableta y computadora", "Los 50 estados y todo Canadá", "Cotización PDF con su logotipo"],
    carteTitre: "Pago por uso",
    carteSous: "Sin suscripción. Paga cuando mide, y nada los meses que no mide.",
    cartePrix: (p) => `${p} por techo`,
    carteBonus: "Primera compra: 2 mediciones gratis adicionales",
    carteChamp: "Monto a depositar",
    carteMin: (m) => `Mínimo ${m}`,
    carteCalc: (n) => `Son ${n} mediciones`,
    carteBtn: "Empezar a medir",
    carteNote: "No guardamos su tarjeta. Sin renovación.",
    aboTitre: "O un plan mensual",
    aboSous: (n) => `Si mide mucho, el plan Solo incluye ${n} mediciones al mes.`,
    aboBascule: (s) => `A partir de ${s} mediciones al mes, Solo le cuesta menos que pagar por techo.`,
    aboBtn: "Ver los planes",
    compTitre: "Lo que cobran los demás",
    comp: [["EagleView", "$15–38 por informe"], ["Roofr", "$19 por informe"],
           ["GAF QuickMeasure", "$18 por informe"], ["RoofSnap", "$49–99 al mes"],
           ["Roof Border", "$9.95 por informe"]],
    compNote: "Precios públicos, septiembre de 2026. Somos el informe pagado más barato del mercado — y el único que no lo compromete a nada.",
    faq: [
      ["¿De verdad no hay suscripción?", "Ninguna. Pone dinero en su cuenta, cada medición descuenta su precio, y cuando se agota no pasa nada — sin renovación, sin tarjeta guardada."],
      ["¿Qué tan preciso es?", "Pruébelo en un techo que ya conoce. Para eso son las dos mediciones gratis de la primera compra: compare nuestros números con los suyos antes de confiar en una cotización real."],
      ["¿Y si el edificio no aparece en la imagen?", "Puede dibujar a mano las construcciones pequeñas — cobertizos, garajes, ampliaciones — y entran en los totales."],
      ["¿Necesito tarjeta para probar?", "Para depositar, sí. También hay una demostración gratuita en nuestra página principal, sin tarjeta y sin registro."],
    ],
    pied: "Mediciones de techos por satélite para contratistas.",
    erreur: "El pago en línea no está disponible. Escríbanos y abriremos su cuenta manualmente.",
  },
};

export default function Mesure() {
  const { lang } = useI18n();
  const L = T[lang] || T.en;
  const fr = lang === "fr";

  // Même ordre de priorité que la page des tarifs : clic du visiteur, puis son
  // pays, puis sa langue. Un couvreur du Texas ne doit pas voir des dollars
  // canadiens : il compare avec ce qu'il connaît.
  const [deviseChoisie, setDeviseChoisie] = useState(null);
  const [devisePays, setDevisePays] = useState(null);
  const devise = deviseChoisie || devisePays || (lang === "fr" ? "cad" : "usd");
  const usd = devise === "usd";

  const [solo, setSolo] = useState(SOLO);
  const prixMesure = usd ? 9.95 : 14;
  const depotMin = usd ? 10 : 14;
  const [montantCarte, setMontantCarte] = useState(depotMin);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    api.get("/public/devise")
      .then(({ data }) => { if (data?.devise) setDevisePays(data.devise); })
      .catch(() => {});
    api.get("/plans")
      .then(({ data }) => {
        const p = (data?.plans || []).find((x) => x.id === "solo");
        if (p) setSolo(p);
      })
      .catch(() => {});
  }, []);

  // Le minimum suit la devise : si le pays arrive après le rendu, le champ doit
  // se réaligner, sinon un visiteur américain verrait 14 comme montant de départ.
  useEffect(() => { setMontantCarte(depotMin); }, [depotMin]);

  const dollars = (v) => (fr ? `${String(v).replace(".", ",")} $` : `$${v}`);
  const soloMois = usd ? (solo.prix_mois_usd ?? 79) : (solo.prix_mois ?? 119);
  const soloQuota = solo.estimates_per_month ?? 40;
  // Seuil de bascule, arrondi vers le haut : le nombre de mesures à partir
  // duquel l'abonnement devient moins cher que l'unité. Affiché tel quel —
  // cacher ce chiffre ferait vendre le mauvais forfait à quelqu'un.
  const seuil = Math.ceil(soloMois / prixMesure);
  const nbMesures = Math.floor(Number(montantCarte || 0) / prixMesure);

  const ouvrirCarte = async () => {
    setEnCours(true);
    const val = Number(montantCarte);
    const monnaie = usd ? "USD" : "CAD";
    evenement("InitiateCheckout", {
      content_name: "carte", content_category: "atterrissage",
      value: val, currency: monnaie,
    });
    evenementGA("begin_checkout", {
      value: val, currency: monnaie,
      items: [{ item_id: "carte", item_name: "Pay as you Go" }],
    });
    try {
      const { data } = await api.post("/public/carte", {
        montant: val, lang, devise: usd ? "usd" : "cad",
      });
      if (data?.url) window.location.href = data.url;
      else throw new Error();
    } catch {
      setEnCours(false);
      toast.error(L.erreur);
      window.location.href = `mailto:${CONTACT}?subject=${encodeURIComponent("Pay as you Go — Roof Border")}`;
    }
  };

  return (
    <div className="min-h-screen bg-app text-ink">
      <header className="border-b border-line">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Marque taille="md" />
          <div className="flex items-center gap-4">
            <a href={TEL_LIEN} className="hidden md:inline-flex items-center gap-1.5 text-sm font-bold text-brand-ink hover:text-brand transition-colors">
              <Phone className="h-4 w-4" /> {TEL_AFFICHE}
            </a>
            <ThemeSwitch />
            <LangSwitch langues={["en", "fr", "es"]} />
            <Link to="/login">
              <Button variant="outline" className="border-line-strong rounded-md hover:bg-hover-strong hover:text-ink h-9">
                {L.connexion}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14">
        {/* accroche : elle reprend mot pour mot la promesse de l'annonce */}
        <div className="text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-ink mb-3">{L.surtitre}</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter mb-4">{L.h1}</h1>
          <p className="text-ink-soft max-w-2xl mx-auto">{L.sous}</p>
        </div>

        {/* sélecteur de devise */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex rounded-md border border-line-strong overflow-hidden" data-testid="bascule-devise">
            {["cad", "usd"].map((d) => (
              <button key={d} type="button" onClick={() => setDeviseChoisie(d)}
                className={`px-4 py-1.5 text-xs font-bold transition-colors ${
                  devise === d ? "bg-orange-600 text-white" : "bg-field text-ink-soft hover:bg-hover"}`}>
                {d === "cad" ? "$ CAN" : "US$"}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-5 gap-6 items-start">
          {/* ── À la Carte : le héros ─────────────────────────────────── */}
          <div className="sm:col-span-3 rounded-xl border border-emerald-500/60 bg-gradient-to-b from-emerald-500/[0.08] to-transparent p-7 shadow-carte">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight">{L.carteTitre}</h2>
            <p className="text-sm text-ink-soft mt-2">{L.carteSous}</p>

            <p className="font-heading text-4xl font-extrabold tracking-tighter mt-5">
              {L.cartePrix(dollars(prixMesure))}
            </p>

            <p className="flex items-start gap-2 text-sm font-semibold text-emerald-500 mt-4">
              <Check className="h-4 w-4 shrink-0 mt-0.5" /> {L.carteBonus}
            </p>

            <label className="block mt-6">
              <span className="text-xs font-bold uppercase tracking-widest text-ink-faint">{L.carteChamp}</span>
              <input
                type="number" min={depotMin} max={200} step="1"
                value={montantCarte}
                onChange={(e) => setMontantCarte(e.target.value)}
                onBlur={(e) => {
                  // On corrige au départ du champ plutôt qu'à la frappe :
                  // corriger pendant la saisie empêche d'effacer pour retaper.
                  const v = Number(e.target.value);
                  if (!v || v < depotMin) setMontantCarte(depotMin);
                  else if (v > 200) setMontantCarte(200);
                }}
                className="mt-1 w-full h-11 px-3 rounded-md border border-line-strong bg-field text-lg font-bold focus:border-orange-500 outline-none"
                data-testid="montant-carte"
              />
              <span className="text-xs text-ink-faint mt-1 block">
                {L.carteMin(dollars(depotMin))}
                {nbMesures > 0 ? ` — ${L.carteCalc(nbMesures)}` : ""}
              </span>
            </label>

            <Button onClick={ouvrirCarte} disabled={enCours}
              className="w-full mt-5 h-12 rounded-md bg-orange-600 hover:bg-orange-700 text-white font-bold text-base"
              data-testid="btn-carte">
              {enCours ? <Loader2 className="h-5 w-5 animate-spin" /> : L.carteBtn}
            </Button>
            <p className="text-xs text-ink-faint text-center mt-3">{L.carteNote}</p>
          </div>

          {/* ── Abonnement : la porte de sortie du gros volume ─────────── */}
          <div className="sm:col-span-2 rounded-xl border border-line bg-surface p-7 shadow-carte">
            <h2 className="font-heading text-xl font-extrabold tracking-tight">{L.aboTitre}</h2>
            <p className="text-sm text-ink-soft mt-2">{L.aboSous(soloQuota)}</p>

            <p className="font-heading text-3xl font-extrabold tracking-tighter mt-5">
              {dollars(soloMois)}<span className="text-base font-bold text-ink-faint">{fr ? " /mois" : " /mo"}</span>
            </p>

            <p className="text-sm text-ink-soft mt-4 border-t border-line pt-4">{L.aboBascule(seuil)}</p>

            <Link to="/pricing">
              <Button variant="outline" className="w-full mt-5 h-11 border-line-strong rounded-md hover:bg-hover-strong hover:text-ink font-semibold">
                {L.aboBtn}
              </Button>
            </Link>
          </div>
        </div>

        {/* preuves */}
        <div className="grid sm:grid-cols-3 gap-3 mt-10">
          {L.preuves.map((p) => (
            <p key={p} className="flex items-start gap-2 text-sm text-ink-soft">
              <Check className="h-4 w-4 shrink-0 mt-0.5 text-emerald-500" /> {p}
            </p>
          ))}
        </div>

        {/* comparaison — prix publics, vérifiables */}
        <section className="mt-14">
          <h2 className="font-heading text-xl font-extrabold tracking-tight mb-4">{L.compTitre}</h2>
          <div className="rounded-xl border border-line bg-surface overflow-hidden">
            {L.comp.map(([nom, prix], i) => (
              <div key={nom}
                className={`flex items-center justify-between px-5 py-3 text-sm ${
                  i < L.comp.length - 1 ? "border-b border-line" : "bg-emerald-500/[0.08] font-bold"}`}>
                <span>{nom}</span>
                <span className={i === L.comp.length - 1 ? "text-emerald-500" : "text-ink-soft"}>{prix}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-ink-faint mt-3">{L.compNote}</p>
        </section>

        {/* questions */}
        <section className="mt-14">
          <div className="space-y-5">
            {L.faq.map(([q, r]) => (
              <div key={q}>
                <h3 className="font-semibold text-sm">{q}</h3>
                <p className="text-sm text-ink-soft mt-1">{r}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-line mt-10">
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-ink-faint">
          <span>{L.pied}</span>
          <SourcePreferee lang={lang} />
          <div className="flex items-center gap-4">
            <a href={TEL_LIEN} className="hover:text-ink transition-colors">{TEL_AFFICHE}</a>
            <a href={`mailto:${CONTACT}`} className="hover:text-ink transition-colors">{CONTACT}</a>
            <Link to="/privacy" className="hover:text-ink transition-colors">{fr ? "Confidentialité" : "Privacy"}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
